import { gql } from '@/__generated__';
import { useMutation, useQuery } from '@apollo/client';
import { ActionIcon, Alert, Group, Loader, Paper, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { IconCheck, IconRefresh, IconShoppingCartShare, IconX } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { sendCheckoutLinksDocument, toggleLiveActiveDocument } from '../graphql/mutations';

const liveDetailsHeaderDocument = gql(/* GraphQL */ `
  query LiveDetailsHeader($liveId: String!) {
    live(where: { id: $liveId }) {
      id
      active
      name
      description
      date
      _count {
        orders
        products
      }
      orders {
        id
        total
      }
    }
  }
`);

export const LiveDetailsHeader = ({ liveId }: { liveId: string }) => {
  const { data, loading, refetch } = useQuery(liveDetailsHeaderDocument, {
    variables: { liveId },
  });

  const [toggleLiveActive, toggleLiveActiveMutation] = useMutation(toggleLiveActiveDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
  });

  const [sendCheckoutLinks, { loading: sendCheckoutLinksLoading }] = useMutation(
    sendCheckoutLinksDocument,
    {
      onCompleted: (data) => {
        showNotification({
          title: 'Sumáre odoslané',
          message: `Sumáre boli odoslané ${data.sendCheckoutLinks} zákazníkom`,
        });
      },
    }
  );

  if (loading) return <Loader />;
  if (!data?.live) return <Alert color="red">Chýbajúce dáta pre LIVE s ID {liveId}</Alert>;

  return (
    <Paper component={Group} justify="space-between" p="md" withBorder>
      <Group>
        <ActionIcon
          variant="filled"
          color={data.live.active ? 'teal' : 'red'}
          onClick={() => void toggleLiveActive({ variables: { where: { id: liveId } } })}
          loading={toggleLiveActiveMutation.loading}
        >
          {data.live.active ? <IconCheck size={16} /> : <IconX size={16} />}
        </ActionIcon>
        <Stack gap={0}>
          <Text>{data.live.name}</Text>
          <Text c="dimmed" size="sm">
            {data.live.description}
          </Text>
          <Text c="dimmed" size="sm">
            {dayjs(data.live.date).format('DD.MM.YYYY')}
          </Text>
        </Stack>
      </Group>

      <Stack gap={0}>
        <Text>{data.live._count.orders} objednávok</Text>
        <Text>{data.live._count.products} produktov</Text>
      </Stack>
      <Group>
        <Text>{data.live.orders?.reduce((sum, { total }) => sum + total, 0)} €</Text>
        <ActionIcon variant="filled" onClick={() => void refetch()} loading={loading}>
          <IconRefresh size={16} />
        </ActionIcon>
      </Group>
      <ActionIcon
        variant="filled"
        color="indigo"
        onClick={() =>
          modals.openConfirmModal({
            title: 'Odoslať sumáre?',
            children: `Odoslať sumáre ${data.live?.orders?.length} zákazníkom?`,
            onConfirm: () => void sendCheckoutLinks({ variables: { where: { id: liveId } } }),
            labels: { confirm: 'Odoslať', cancel: 'Nie' },
          })
        }
        loading={sendCheckoutLinksLoading}
      >
        <IconShoppingCartShare size={16} />
      </ActionIcon>
    </Paper>
  );
};
